import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import OpcSplitPanels from "../_shared/OpcSplitPanels/OpcSplitPanels";
import OpcChatPanel from "../_shared/OpcChatPanel/OpcChatPanel";
import { OpcPageError } from "../_shared/OpcPageState/OpcPageState";
import { OpcChatSkeleton } from "../_shared/OpcChatView/OpcChatView";
import { OpcPanelSkeleton } from "../_shared/OpcSkeleton/OpcSkeleton";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import okrPlanCoachChatStore, {
  COACH_CARD,
  COACH_CHAT_EMIT,
} from "../_socket/okrPlanCoachChatStore";
import useCoachPlan from "../_hooks/useCoachPlan";
import { COACH_FEATURE_SLUG } from "../_config/okrPlansCoachConfig";
import { firstNameOf, reportName } from "../_config/okrPlansCoachFormat";
import {
  companyPath,
  dashboardPath,
  planPath,
  reportPath,
} from "../_config/okrPlansCoachRoutes";
import CheckInReport from "./CheckInReport/CheckInReport";

// ═══════════════════════════════════════════════════════════════════════════
// Weekly check-in (coach) — BE 2.7 / 2.8; CD19, CD23. Chat with the check-in
// agent on the left (coach-mode `/new-chat`, `okr_plan_coach`), the report on
// the right (draggable divider). The remark is saved from the chat's remark
// card; the report shows it after `okr_plan_remark_saved`. Opening the
// check-in is `POST …/reports/:id/check-in` (idempotent). A closed plan
// shows the report and an existing check-in chat read-only.
// ═══════════════════════════════════════════════════════════════════════════

const selectLastCompany = (s) => s.lastCompany;

const OkrPlansCoachCheckIn = () => {
  const { planUniqueId: planId, reportUniqueId: reportId } = useParams();
  const history = useHistory();
  const { plan } = useCoachPlan(planId);
  const lastCompany = useOkrPlansCoachStore(selectLastCompany);
  const selectReport = useCallback((s) => s.reports[reportId], [reportId]);
  const report = useOkrPlansCoachStore(selectReport);
  const [chatId, setChatId] = useState(null);
  const [error, setError] = useState(null);

  const closed =
    !!plan &&
    (plan.is_archived ||
      plan.quarter?.is_read_only ||
      ["REVIEWED", "NOT_REVIEWED"].includes(plan.review?.state));

  const load = useCallback(() => {
    setError(null);
    setChatId(null);
    okrPlansCoachStore
      .getState()
      .fetchReport(planId, reportId)
      .catch((err) => setError(err));
  }, [planId, reportId]);
  useEffect(load, [load]);

  // The check-in chat: the report's own, or open one (not on a closed plan).
  useEffect(() => {
    if (!report || !plan) return;
    if (report.check_in_chat_unique_id) {
      setChatId(report.check_in_chat_unique_id);
      return;
    }
    if (closed) return;
    okrPlansCoachStore
      .getState()
      .startCheckIn(reportId)
      .then((res) => setChatId(res?.chat_unique_id || null))
      .catch((err) => setError(err));
  }, [report, plan, closed, reportId]);

  const owner = firstNameOf(plan?.owner?.name) || "The owner";
  const toPlan = () => history.push(planPath(planId));
  const crumbs = [
    { title: "Dashboard", go: () => history.push(dashboardPath()) },
    ...(lastCompany
      ? [
          {
            title: lastCompany.name,
            go: () =>
              history.push(
                companyPath(lastCompany.id, lastCompany.quarterCompanyId),
              ),
          },
        ]
      : []),
    { title: plan?.objective?.text || "OKR plan", go: toPlan },
    {
      title: report
        ? `${reportName(report.report_number)} check-in`
        : "Check-in",
    },
  ];

  const onCard = (kind, card) => {
    const chat = okrPlanCoachChatStore.getState();
    if (kind !== COACH_CARD.REMARK) return {};
    return {
      onConfirm: () =>
        chat.cardAction(chatId, card.message_id, COACH_CHAT_EMIT.REMARK_SAVE),
      onDecline: () =>
        chat.cardAction(
          chatId,
          card.message_id,
          COACH_CHAT_EMIT.REMARK_KEEP_EDITING,
        ),
    };
  };

  let content;
  if (error)
    content = (
      <OpcPageError
        error={error}
        onRetry={load}
        onBack={toPlan}
        backLabel="Plan"
      />
    );
  else if (!report || !plan)
    // CD50: the real split layout with the chat skeleton from the start —
    // one skeleton until the messages are in.
    content = (
      <OpcSplitPanels
        id="project-checkin"
        left={<OpcChatSkeleton />}
        right={<OpcPanelSkeleton />}
      />
    );
  else {
    // The check-in chat is being opened → still the chat skeleton (CD50).
    const starting = !chatId && (!closed || !!report.check_in_chat_unique_id);
    const left = starting ? (
      <OpcChatSkeleton />
    ) : chatId ? (
      <OpcChatPanel
        chatId={chatId}
        featureSlug={COACH_FEATURE_SLUG.CHECK_IN}
        owner={{ planId, reportId }}
        readOnly={closed}
        starters={[
          "Draft my remark",
          "What changed since last week?",
          `What should ${owner} focus on next?`,
        ]}
        onCard={onCard}
        footerNote={
          closed ? (
            <span className="small light-text">
              This plan is closed — the check-in is kept for your records.
            </span>
          ) : null
        }
      />
    ) : (
      <div className="d-flex align-items-center justify-content-center h-100 px-4 text-center">
        <span className="light-text small">
          No check-in was done on this report.
        </span>
      </div>
    );
    const right = (
      <CheckInReport
        report={report}
        plan={plan}
        objective={plan.objective?.text}
        owner={owner}
        onPrev={
          report.prev_report_unique_id
            ? () =>
                history.push(reportPath(planId, report.prev_report_unique_id))
            : null
        }
        onNext={
          report.next_report_unique_id
            ? () =>
                history.push(reportPath(planId, report.next_report_unique_id))
            : null
        }
      />
    );
    content = <OpcSplitPanels id="project-checkin" left={left} right={right} />;
  }

  return (
    <div className="opc-screen">
      <OpcHeader crumbs={crumbs} />
      {content}
    </div>
  );
};

export default OkrPlansCoachCheckIn;
